import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { ThemedDatePicker } from '../../components/ThemedDatePicker';
import {
  cancelMyTimeBlock,
  createMyTimeBlock,
  getMe,
  listMyTimeBlocks,
  listProBookings,
  updateMe,
  type ProBookingRow,
  type ProTimeBlock,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

// Walk back from the 1st to Monday so the calendar grid starts on a
// Monday (matches the day-header row above the grid).
function startOfMonthGrid(month: Date): Date {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // 0 for Mon, 6 for Sun
  first.setDate(first.getDate() - offset);
  return first;
}

/**
 * Parse a backend `date`-column value (YYYY-MM-DD string or full
 * ISO datetime) as a LOCAL-time midnight, so display and date math
 * stay on the calendar date the pro picked. `new Date('2026-06-10')`
 * is interpreted as UTC midnight by JS — in BST that's 23:00 on the
 * 9th locally, and toLocaleDateString / setDate / setHours all then
 * land on the wrong day (audit 2026-06-04, customers + pros saw pro
 * holidays off-by-one during UK summer).
 */
function parseCalendarDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    // Strip the time component so subsequent setDate iteration is
    // stable across DST boundaries.
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  // Construct in LOCAL time so toLocaleDateString prints the same
  // calendar date that's in the string.
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Per-day slot (HH:MM strings, 24h). Multiple slots per day are
// supported by the backend (split shifts), so we model an array.
type Slot = { from: string; to: string };

// Display label → backend day_of_week. Backend uses 0=Sun..6=Sat,
// matches JS Date#getDay().
const DAYS: Array<{ label: string; dow: number }> = [
  { label: 'Mon', dow: 1 },
  { label: 'Tue', dow: 2 },
  { label: 'Wed', dow: 3 },
  { label: 'Thu', dow: 4 },
  { label: 'Fri', dow: 5 },
  { label: 'Sat', dow: 6 },
  { label: 'Sun', dow: 0 },
];

// Max travel radius (miles). Mirrors the sign-up flow cap — beyond this is
// unrealistic given the 1h buffer the platform allows between appointments.
const MAX_RADIUS_MILES = 30;

// Half-hour grid 06:00–22:00. Easier than typing freeform.
const TIME_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let h = 6; h <= 22; h += 1) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    if (h < 22) out.push(`${String(h).padStart(2, '0')}:30`);
  }
  return out;
})();

// Compare HH:MM strings as minutes-since-midnight. Lexicographic
// works too because zero-padded, but minute math is clearer.
const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

// Pad an HH:MM:SS string from the backend down to HH:MM for the
// dropdown to match.
const trimSeconds = (hhmmss: string): string => {
  if (!hhmmss) return '';
  return hhmmss.slice(0, 5);
};

// A confirmed patch-test appointment is stored as a partial-day time block
// with a "Patch test — …" reason. Detect it so the schedule renders it as a
// distinct event (not a generic "Holiday" block) and keeps it out of the
// holidays list.
const isPatchTestBlock = (b: { reason?: string | null }): boolean =>
  (b.reason ?? '').toLowerCase().startsWith('patch test');

export default function ProSchedulePage() {
  const navigate = useNavigate();
  // Show a back link when opened from Account Settings (which passes
  // returnTo). Reached via the top nav there's no state, so no button.
  const location = useLocation();
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ?? null;
  const [view, setView] = useState<'weekly' | 'monthly'>('monthly');
  // Start on the current month so the grid is meaningful for new
  // pros — was previously hard-coded to September 2026.
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  // Availability state: one entry per day label, each carrying its
  // (possibly empty) list of slots. Empty list = unavailable.
  const [slotsByDay, setSlotsByDay] = useState<Record<string, Slot[]>>(
    () => Object.fromEntries(DAYS.map((d) => [d.label, []])),
  );
  const [availLoading, setAvailLoading] = useState(true);
  const [availSaving, setAvailSaving] = useState(false);
  const [availError, setAvailError] = useState<string | null>(null);

  // Same-day (last-minute) booking toggle. Default true so the switch
  // doesn't flash "off" before the profile loads.
  const [sameDayEnabled, setSameDayEnabled] = useState(true);
  const [sameDaySaving, setSameDaySaving] = useState(false);

  // Travel radius (miles). Editable here so pros don't have to walk back
  // through the sign-up flow. Capped at MAX_RADIUS_MILES — unrealistic
  // beyond that given the 1h inter-appointment buffer.
  const [radiusMiles, setRadiusMiles] = useState(0);
  const [radiusInitial, setRadiusInitial] = useState(0);
  const [radiusSaving, setRadiusSaving] = useState(false);

  // Holiday form state.
  const [holidayFrom, setHolidayFrom] = useState('');
  const [holidayUntil, setHolidayUntil] = useState('');
  const [holidayReason, setHolidayReason] = useState('');
  // Partial-day blocking: when on, the block covers only [start,end]
  // on each date in range (e.g. a 2pm–4pm external appointment).
  const [holidayPartialDay, setHolidayPartialDay] = useState(false);
  const [holidayStartTime, setHolidayStartTime] = useState('');
  const [holidayEndTime, setHolidayEndTime] = useState('');
  const [timeBlocks, setTimeBlocks] = useState<ProTimeBlock[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(true);
  const [bookings, setBookings] = useState<ProBookingRow[]>([]);
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [holidayError, setHolidayError] = useState<string | null>(null);
  const [holidayMessage, setHolidayMessage] = useState<string | null>(null);

  const loadBlocks = async () => {
    setBlocksLoading(true);
    try {
      const res = await listMyTimeBlocks();
      setTimeBlocks(res.data ?? []);
    } catch (err) {
      setHolidayError(apiErrorMessage(err, 'Could not load your holidays.'));
    } finally {
      setBlocksLoading(false);
    }
  };

  // Pull upcoming + recent bookings so the calendar shows real
  // appointments. We ask for a wide page (100) and filter client-side
  // by month — typical pros have <100 active bookings at a time.
  const loadBookings = async () => {
    try {
      const res = await listProBookings({ limit: 100, offset: 0 });
      setBookings(res.data ?? []);
    } catch {
      // Non-fatal — calendar still renders availability + holidays.
    }
  };

  // Pull the pro's saved weekly availability and project it onto the
  // by-day slot map. Empty days stay empty.
  const loadAvailability = async () => {
    setAvailLoading(true);
    setAvailError(null);
    try {
      const me = await getMe();
      const rows: Array<{
        day_of_week: number;
        start_time: string;
        end_time: string;
        is_active: boolean;
      }> = me?.info?.availabilities ?? [];
      const next: Record<string, Slot[]> = Object.fromEntries(
        DAYS.map((d) => [d.label, [] as Slot[]]),
      );
      for (const row of rows) {
        if (!row.is_active) continue;
        const def = DAYS.find((d) => d.dow === row.day_of_week);
        if (!def) continue;
        next[def.label].push({
          from: trimSeconds(row.start_time),
          to: trimSeconds(row.end_time),
        });
      }
      // Sort slots per day so the UI reads top-down chronologically.
      for (const key of Object.keys(next)) {
        next[key].sort((a, b) => toMinutes(a.from) - toMinutes(b.from));
      }
      setSlotsByDay(next);
      // Treat a missing flag as "on" (default for existing pros). The API
      // may serialise the tinyint as 0/1 OR false/true, so treat both 0 and
      // false as "off" — `!== false` alone would let a numeric 0 read as "on"
      // and bounce the toggle back ON after a save + refresh.
      const rawSameDay = me?.info?.same_day_bookings_enabled;
      setSameDayEnabled(rawSameDay !== false && rawSameDay !== 0);
      // Clamp any pre-cap saved value (e.g. legacy 500) down to the cap.
      const savedRadius = Math.min(
        Math.max(0, Math.round(Number(me?.info?.travel_radius_miles ?? 0))),
        MAX_RADIUS_MILES,
      );
      setRadiusMiles(savedRadius);
      setRadiusInitial(savedRadius);
    } catch (err) {
      setAvailError(apiErrorMessage(err, 'Could not load availability.'));
    } finally {
      setAvailLoading(false);
    }
  };

  // Flip the same-day toggle and persist immediately (applies live — no
  // admin approval). Optimistic with rollback on failure.
  const handleToggleSameDay = async () => {
    const next = !sameDayEnabled;
    setSameDayEnabled(next);
    setSameDaySaving(true);
    try {
      await updateMe({ same_day_bookings_enabled: next });
    } catch (err) {
      setSameDayEnabled(!next);
      toast.error(
        apiErrorMessage(err, 'Could not update same-day availability.'),
      );
    } finally {
      setSameDaySaving(false);
    }
  };

  // Persist the travel radius (applies live — no admin round-trip).
  const handleSaveRadius = async () => {
    const value = Math.min(Math.max(0, radiusMiles), MAX_RADIUS_MILES);
    setRadiusSaving(true);
    try {
      await updateMe({ travel_radius_miles: value });
      setRadiusInitial(value);
      toast.success('Travel radius updated.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update your travel radius.'));
    } finally {
      setRadiusSaving(false);
    }
  };

  useEffect(() => {
    loadBlocks();
    loadBookings();
    loadAvailability();
  }, []);

  const handleAddHoliday = async () => {
    setHolidayError(null);
    setHolidayMessage(null);
    if (!holidayFrom || !holidayUntil) {
      setHolidayError('Pick both a start and end date.');
      return;
    }
    if (new Date(holidayUntil) < new Date(holidayFrom)) {
      setHolidayError('The end date must be on or after the start date.');
      return;
    }
    if (holidayPartialDay) {
      if (!holidayStartTime || !holidayEndTime) {
        setHolidayError('Pick both a start and end time, or turn off "specific hours".');
        return;
      }
      if (holidayEndTime <= holidayStartTime) {
        setHolidayError('The end time must be after the start time.');
        return;
      }
    }
    setSavingHoliday(true);
    try {
      await createMyTimeBlock({
        from_date: holidayFrom,
        to_date: holidayUntil,
        reason: holidayReason.trim() || null,
        start_time: holidayPartialDay ? holidayStartTime : null,
        end_time: holidayPartialDay ? holidayEndTime : null,
      });
      setHolidayMessage('Saved.');
      setHolidayFrom('');
      setHolidayUntil('');
      setHolidayReason('');
      setHolidayPartialDay(false);
      setHolidayStartTime('');
      setHolidayEndTime('');
      await loadBlocks();
    } catch (err) {
      setHolidayError(apiErrorMessage(err, 'Could not save your holiday.'));
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleCancelHoliday = async (id: string) => {
    setHolidayError(null);
    setHolidayMessage(null);
    setCancellingId(id);
    try {
      await cancelMyTimeBlock(id);
      setHolidayMessage('Holiday cancelled.');
      await loadBlocks();
    } catch (err) {
      setHolidayError(apiErrorMessage(err, 'Could not cancel this holiday.'));
    } finally {
      setCancellingId(null);
    }
  };

  const formatRange = (b: ProTimeBlock) => {
    try {
      // Local-midnight parsing — `new Date('2026-06-10')` would be
      // interpreted as UTC midnight and print as 9 Jun in BST.
      const fmt = (s: string) => {
        const d = parseCalendarDate(s);
        return d
          ? d.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : s;
      };
      const dateRange = `${fmt(b.from_date)} – ${fmt(b.to_date)}`;
      if (b.start_time && b.end_time) {
        const hm = (t: string) => t.slice(0, 5); // HH:MM:SS -> HH:MM
        return `${dateRange} · ${hm(b.start_time)}–${hm(b.end_time)}`;
      }
      return dateRange;
    } catch {
      return `${b.from_date} – ${b.to_date}`;
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Toggle a day on/off. "Off" = empty slot list (every existing slot
  // dropped). "On" = if currently empty, seed with a 09:00–17:00 slot.
  const toggleDay = (dayLabel: string) => {
    setSlotsByDay((prev) => {
      const current = prev[dayLabel] ?? [];
      if (current.length > 0) return { ...prev, [dayLabel]: [] };
      return { ...prev, [dayLabel]: [{ from: '09:00', to: '17:00' }] };
    });
  };

  const updateSlot = (
    dayLabel: string,
    index: number,
    patch: Partial<Slot>,
  ) => {
    setSlotsByDay((prev) => {
      const list = (prev[dayLabel] ?? []).slice();
      list[index] = { ...list[index], ...patch };
      return { ...prev, [dayLabel]: list };
    });
  };

  const addSlot = (dayLabel: string) => {
    setSlotsByDay((prev) => {
      const list = (prev[dayLabel] ?? []).slice();
      // Default the new slot to start right after the last one ends
      // (or 09:00 if there are none) so the user rarely needs to
      // adjust both fields.
      const lastEnd =
        list.length > 0 ? toMinutes(list[list.length - 1].to) : 9 * 60;
      const fromMin = Math.min(lastEnd, 22 * 60 - 30);
      const toMin = Math.min(fromMin + 60, 22 * 60);
      const hhmm = (m: number) =>
        `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
      list.push({ from: hhmm(fromMin), to: hhmm(toMin) });
      return { ...prev, [dayLabel]: list };
    });
  };

  const removeSlot = (dayLabel: string, index: number) => {
    setSlotsByDay((prev) => {
      const list = (prev[dayLabel] ?? []).slice();
      list.splice(index, 1);
      return { ...prev, [dayLabel]: list };
    });
  };

  // Per-slot validation flags, computed once for the whole map. A
  // slot is "bad" if from >= to, "overlap" if a previous slot on the
  // same day's [from,to) intersects it.
  const slotErrors = useMemo(() => {
    const errs: Record<string, Array<string | null>> = {};
    for (const def of DAYS) {
      const list = slotsByDay[def.label] ?? [];
      const dayErrs: Array<string | null> = list.map(() => null);
      // Single-slot validation: from must be strictly before to.
      list.forEach((s, i) => {
        if (s.from && s.to && toMinutes(s.from) >= toMinutes(s.to)) {
          dayErrs[i] = 'End must be after start.';
        }
      });
      // Overlap check: sort by start, walk and compare against running
      // max end. Marks the second-onwards slot of any overlapping pair.
      const indexed = list
        .map((s, i) => ({ ...s, i }))
        .sort((a, b) => toMinutes(a.from) - toMinutes(b.from));
      let prevEnd = -1;
      let prevIdx = -1;
      for (const s of indexed) {
        if (dayErrs[s.i]) continue;
        if (toMinutes(s.from) < prevEnd) {
          dayErrs[s.i] = 'Overlaps another slot on the same day.';
          if (prevIdx >= 0 && !dayErrs[prevIdx]) {
            dayErrs[prevIdx] = 'Overlaps another slot on the same day.';
          }
        }
        if (toMinutes(s.to) > prevEnd) {
          prevEnd = toMinutes(s.to);
          prevIdx = s.i;
        }
      }
      errs[def.label] = dayErrs;
    }
    return errs;
  }, [slotsByDay]);

  const hasAnyError = useMemo(
    () => Object.values(slotErrors).some((arr) => arr.some(Boolean)),
    [slotErrors],
  );

  const handleSaveAvailability = async () => {
    if (hasAnyError || availSaving) return;
    setAvailSaving(true);
    setAvailError(null);
    try {
      const availabilities: Array<{
        day_of_week: number;
        start_time: string;
        end_time: string;
        is_active: boolean;
      }> = [];
      for (const def of DAYS) {
        for (const slot of slotsByDay[def.label] ?? []) {
          if (!slot.from || !slot.to) continue;
          availabilities.push({
            day_of_week: def.dow,
            // Backend stores `time` so HH:MM:SS is the safe canonical
            // format. The schedule grid only deals in HH:MM.
            start_time: `${slot.from}:00`,
            end_time: `${slot.to}:00`,
            is_active: true,
          });
        }
      }
      await updateMe({ availabilities });
      toast.success('Availability saved.');
      await loadAvailability();
    } catch (err) {
      setAvailError(apiErrorMessage(err, 'Could not save availability.'));
    } finally {
      setAvailSaving(false);
    }
  };

  const days = DAYS.map((d) => d.label); // Mon..Sun

  // Group real bookings by YYYY-MM-DD so the calendar cells can pluck
  // their events in O(1). Only live bookings show — cancelled rows
  // would just clutter the view.
  const bookingsByDate = useMemo(() => {
    const map = new Map<string, ProBookingRow[]>();
    const LIVE = new Set(['CONFIRMED', 'IN_PROGRESS', 'COMPLETED']);
    for (const b of bookings) {
      if (!LIVE.has(b.status)) continue;
      try {
        const d = new Date(b.scheduled_start);
        if (Number.isNaN(d.getTime())) continue;
        const key = ymd(d);
        const prev = map.get(key) ?? [];
        prev.push(b);
        map.set(key, prev);
      } catch {
        // skip
      }
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          new Date(a.scheduled_start).getTime() -
          new Date(b.scheduled_start).getTime(),
      );
    }
    return map;
  }, [bookings]);

  // Confirmed patch tests, grouped by date, for the distinct calendar chip.
  const patchTestsByDate = useMemo(() => {
    const map = new Map<string, Array<{ id: string; time: string }>>();
    for (const b of timeBlocks) {
      if (b.status !== 'ACTIVE' || !isPatchTestBlock(b)) continue;
      const start = parseCalendarDate(b.from_date);
      if (!start) continue;
      const key = ymd(start);
      const prev = map.get(key) ?? [];
      prev.push({ id: b.id, time: b.start_time ? trimSeconds(b.start_time) : '' });
      map.set(key, prev);
    }
    return map;
  }, [timeBlocks]);

  const blockedDates = useMemo(() => {
    const set = new Set<string>();
    for (const b of timeBlocks) {
      if (b.status !== 'ACTIVE') continue;
      // Patch tests render as their own distinct chip, not a grey "Holiday"
      // day — skip them here (real availability blocking is server-side).
      if (isPatchTestBlock(b)) continue;
      // Local-midnight parsing so the day-by-day iterator lands on
      // the same calendar dates the pro selected — without this, BST
      // shifts the start cursor to the previous day and the loop
      // blocks "10 Jun – 12 Jun" as "9 Jun – 11 Jun" on the calendar.
      const start = parseCalendarDate(b.from_date);
      const end = parseCalendarDate(b.to_date);
      if (!start || !end) continue;
      const cursor = new Date(start.getTime());
      while (cursor.getTime() <= end.getTime()) {
        set.add(ymd(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return set;
  }, [timeBlocks]);

  const calendarDays = useMemo(() => {
    const start = startOfMonthGrid(currentDate);
    const cells: Array<{
      day: number;
      month: 'prev' | 'curr' | 'next';
      iso: string;
      isBlocked: boolean;
      isAvailable: boolean;
    }> = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start.getTime());
      d.setDate(d.getDate() + i);
      const inMonth = d.getMonth() === currentDate.getMonth();
      const dayKey = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][
        d.getDay()
      ];
      const dayAvail = (slotsByDay[dayKey] ?? []).length > 0;
      const iso = ymd(d);
      cells.push({
        day: d.getDate(),
        month: inMonth
          ? 'curr'
          : d.getTime() < currentDate.getTime()
            ? 'prev'
            : 'next',
        iso,
        isBlocked: blockedDates.has(iso),
        isAvailable: inMonth && dayAvail,
      });
    }
    return cells;
  }, [currentDate, slotsByDay, blockedDates]);

  // Weekly view: pin to the Monday of `weekAnchor`'s week and emit 7
  // cells. The cells reuse the same shape as monthly so the rendering
  // code below can stay unchanged.
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const weekStart = useMemo(() => {
    const d = new Date(weekAnchor.getTime());
    d.setHours(0, 0, 0, 0);
    const offset = (d.getDay() + 6) % 7; // 0 for Mon, 6 for Sun
    d.setDate(d.getDate() - offset);
    return d;
  }, [weekAnchor]);

  const weekDays = useMemo(() => {
    const cells: Array<{
      day: number;
      month: 'prev' | 'curr' | 'next';
      iso: string;
      isBlocked: boolean;
      isAvailable: boolean;
    }> = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(weekStart.getTime());
      d.setDate(d.getDate() + i);
      const dayKey = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][
        d.getDay()
      ];
      const dayAvail = (slotsByDay[dayKey] ?? []).length > 0;
      const iso = ymd(d);
      cells.push({
        day: d.getDate(),
        month: 'curr',
        iso,
        isBlocked: blockedDates.has(iso),
        isAvailable: dayAvail,
      });
    }
    return cells;
  }, [weekStart, slotsByDay, blockedDates]);

  const handlePrevWeek = () => {
    setWeekAnchor((prev) => {
      const d = new Date(prev.getTime());
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const handleNextWeek = () => {
    setWeekAnchor((prev) => {
      const d = new Date(prev.getTime());
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  // Header label for the weekly view: "May 18 – 24, 2026" style range.
  const weekRangeLabel = useMemo(() => {
    const end = new Date(weekStart.getTime());
    end.setDate(end.getDate() + 6);
    const sameMonth = end.getMonth() === weekStart.getMonth();
    const sameYear = end.getFullYear() === weekStart.getFullYear();
    const monthShort = (d: Date) =>
      d.toLocaleString('default', { month: 'short' });
    if (sameMonth && sameYear) {
      return `${monthShort(weekStart)} ${weekStart.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
    }
    if (sameYear) {
      return `${monthShort(weekStart)} ${weekStart.getDate()} – ${monthShort(end)} ${end.getDate()}, ${end.getFullYear()}`;
    }
    return `${monthShort(weekStart)} ${weekStart.getDate()}, ${weekStart.getFullYear()} – ${monthShort(end)} ${end.getDate()}, ${end.getFullYear()}`;
  }, [weekStart]);

  return (
    <div className="min-h-screen bg-white flex flex-col font-['DM_Sans',sans-serif]">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            {returnTo ? (
              <Link
                to={returnTo}
                aria-label="Back"
                className="w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
              >
                <span className="material-icons text-[#908e8e] text-[18px]">
                  chevron_left
                </span>
              </Link>
            ) : null}
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[24px] text-[#8e2a2f]">
              Schedule
            </h1>
          </div>
          <div className="flex bg-[#f7f7f7] p-1.5 rounded-[14px] w-fit">
            <button
              onClick={() => setView('weekly')}
              className={`px-10 py-2.5 rounded-[11px] text-[15px] font-bold transition-all ${view === 'weekly' ? 'bg-[#8e2a2f] text-white shadow-md' : 'bg-transparent text-[#908e8e] hover:text-[#3a3335]'}`}
            >
              Weekly
            </button>
            <button
              onClick={() => setView('monthly')}
              className={`px-10 py-2.5 rounded-[11px] text-[15px] font-bold transition-all ${view === 'monthly' ? 'bg-[#8e2a2f] text-white shadow-md' : 'bg-transparent text-[#908e8e] hover:text-[#3a3335]'}`}
            >
              Monthly
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-0">
          {/* Calendar Section */}
          <div className="border border-[#eee] rounded-l-xl overflow-hidden border-r-0">
            {/* Calendar Header */}
            <div className="px-6 py-6 border-b border-[#eee] flex items-center justify-between">
              <h2 className="font-bold text-[18px] text-[#3a3335]">
                {view === 'monthly'
                  ? `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`
                  : weekRangeLabel}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={view === 'monthly' ? handlePrevMonth : handlePrevWeek}
                  className="w-8 h-8 flex items-center justify-center bg-[#f3f4f6] rounded-md hover:bg-[#eee] transition-colors"
                >
                  <span className="material-icons text-[18px] text-[#908e8e]">
                    chevron_left
                  </span>
                </button>
                <button
                  onClick={view === 'monthly' ? handleNextMonth : handleNextWeek}
                  className="w-8 h-8 flex items-center justify-center bg-[#f3f4f6] rounded-md hover:bg-[#eee] transition-colors"
                >
                  <span className="material-icons text-[18px] text-[#908e8e]">
                    chevron_right
                  </span>
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 border-b border-[#eee]">
              {days.map((d, i) => {
                const label =
                  view === 'weekly'
                    ? (() => {
                        const cell = new Date(weekStart.getTime());
                        cell.setDate(cell.getDate() + i);
                        return `${d} ${cell.getDate()}`;
                      })()
                    : d;
                return (
                  <div
                    key={d}
                    className="py-2 text-center border-r border-[#eee] last:border-r-0"
                  >
                    <span className="text-[#908e8e] text-[12px] font-bold">{label}</span>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-7">
              {(view === 'monthly' ? calendarDays : weekDays).map((day, idx) => {
                const cellBg =
                  day.month !== 'curr'
                    ? 'bg-[#F7F7F7]'
                    : day.isBlocked
                      ? 'bg-[#fbeaea]'
                      : 'bg-white';
                const dayBookings =
                  day.month === 'curr'
                    ? bookingsByDate.get(day.iso) ?? []
                    : [];
                const hasBookings = dayBookings.length > 0;
                const bottomStrip = day.isBlocked
                  ? 'bg-[#e29393]'
                  : hasBookings
                    ? 'bg-[#8e2a2f]'
                    : day.isAvailable
                      ? 'bg-[#98c2a3]'
                      : '';
                const cellMinH = view === 'weekly' ? 'min-h-[420px]' : 'min-h-[120px]';
                const maxShow = view === 'weekly' ? 8 : 2;
                return (
                  <div
                    key={idx}
                    className={`${cellMinH} py-2 px-0 border-r border-b border-[#eee] last:border-r-0 relative ${cellBg}`}
                  >
                    <span
                      className={`text-[12px] font-bold block text-right pr-2 mb-2 ${day.month !== 'curr' ? 'text-[#ccc]' : 'text-[#3a3335]'}`}
                    >
                      {day.day}
                    </span>

                    {day.month === 'curr' && day.isBlocked ? (
                      <div className="mx-2 bg-[#8e2a2f]/10 text-[#8e2a2f] text-[10px] p-1.5 rounded font-bold leading-tight truncate">
                        Holiday
                      </div>
                    ) : null}

                    {!day.isBlocked && dayBookings.length > 0 ? (
                      <div className="mx-2 space-y-1">
                        {dayBookings.slice(0, maxShow).map((b) => {
                          const t = new Date(b.scheduled_start);
                          const time = t.toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          });
                          return (
                            <button
                              key={b.id}
                              type="button"
                              onClick={() =>
                                navigate(`/professional/bookings/${b.id}`)
                              }
                              className="block w-full text-left bg-[#8e2a2f] text-white text-[10px] p-1.5 rounded font-bold leading-tight truncate hover:bg-[#7a2228] transition-colors"
                              title={`${time} · ${b.service?.name ?? 'Booking'}`}
                            >
                              {time} · {b.service?.name ?? 'Booking'}
                            </button>
                          );
                        })}
                        {dayBookings.length > maxShow ? (
                          <p className="text-[10px] text-[#908e8e] pl-1">
                            +{dayBookings.length - maxShow} more
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {/* Patch-test appointments — a distinct amber chip,
                        separate from bookings and holidays. */}
                    {day.month === 'curr' &&
                    (patchTestsByDate.get(day.iso)?.length ?? 0) > 0 ? (
                      <div className="mx-2 mt-1 space-y-1">
                        {(patchTestsByDate.get(day.iso) ?? []).map((p) => (
                          <div
                            key={p.id}
                            title={`${p.time ? p.time + ' · ' : ''}Patch test`}
                            className="flex items-center gap-1 bg-[#fff7e6] text-[#9a6700] ring-1 ring-inset ring-[#f3d6a0] text-[10px] p-1.5 rounded font-bold leading-tight"
                          >
                            <span className="material-icons text-[12px]">
                              event_available
                            </span>
                            <span className="truncate">
                              {p.time ? `${p.time} · ` : ''}Patch test
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {bottomStrip ? (
                      <div
                        className={`absolute bottom-0 left-0 right-0 h-[2px] ${bottomStrip}`}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-[#fcfaf7] border border-[#eee] rounded-r-xl p-6 h-full">
              <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
                Availability settings
              </h3>
              <p className="text-[#908e8e] text-[13px] mb-6">
                Set the hours customers can book you
              </p>

              {availError ? (
                <p className="mb-3 text-[#991b1b] text-[12px]">{availError}</p>
              ) : null}

              <div className="mb-6">
                <p className="font-bold text-[#3a3335] text-[14px] mb-4">
                  Available days
                </p>

                {availLoading ? (
                  <p className="text-[#908e8e] text-[12px]">Loading…</p>
                ) : (
                  <div className="space-y-5">
                    {DAYS.map((def) => {
                      const dayLabel = def.label;
                      const slots = slotsByDay[dayLabel] ?? [];
                      const enabled = slots.length > 0;
                      const dayErrs = slotErrors[dayLabel] ?? [];
                      return (
                        <div key={dayLabel}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => toggleDay(dayLabel)}
                                className={`w-[18px] h-[18px] rounded-[4px] border flex items-center justify-center transition-colors ${enabled ? 'bg-[#8e2a2f] border-[#8e2a2f]' : 'border-[#908E8E]'}`}
                                aria-label={`Toggle ${dayLabel}`}
                              >
                                {enabled ? (
                                  <span className="material-icons text-white text-[10px]">
                                    check
                                  </span>
                                ) : null}
                              </button>
                              <span className="text-[#3a3335] font-bold text-[13px]">
                                {dayLabel}
                              </span>
                            </div>
                            {!enabled ? (
                              <span className="text-[#908e8e] text-[12px] font-bold">
                                Unavailable
                              </span>
                            ) : null}
                          </div>

                          {enabled ? (
                            <div className="ml-[30px] space-y-2">
                              {slots.map((slot, i) => {
                                const err = dayErrs[i];
                                return (
                                  <div key={i}>
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={slot.from}
                                        onChange={(e) =>
                                          updateSlot(dayLabel, i, {
                                            from: e.target.value,
                                          })
                                        }
                                        className={`flex-1 bg-white border rounded px-2 py-1 text-[12px] font-bold text-[#3a3335] outline-none ${err ? 'border-red-400' : 'border-[#eee] focus:border-[#8e2a2f]'}`}
                                      >
                                        {TIME_OPTIONS.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                      </select>
                                      <span className="text-[#908e8e] text-[12px]">
                                        to
                                      </span>
                                      <select
                                        value={slot.to}
                                        onChange={(e) =>
                                          updateSlot(dayLabel, i, {
                                            to: e.target.value,
                                          })
                                        }
                                        className={`flex-1 bg-white border rounded px-2 py-1 text-[12px] font-bold text-[#3a3335] outline-none ${err ? 'border-red-400' : 'border-[#eee] focus:border-[#8e2a2f]'}`}
                                      >
                                        {TIME_OPTIONS.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() => removeSlot(dayLabel, i)}
                                        className="text-[#908e8e] hover:text-[#8e2a2f]"
                                        aria-label="Remove slot"
                                      >
                                        <span className="material-icons text-[18px]">
                                          close
                                        </span>
                                      </button>
                                    </div>
                                    {err ? (
                                      <p className="text-[11px] text-red-600 mt-1">
                                        {err}
                                      </p>
                                    ) : null}
                                  </div>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() => addSlot(dayLabel)}
                                className="text-[#8e2a2f] text-[12px] font-bold hover:underline"
                              >
                                + Add another slot
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSaveAvailability}
                  disabled={availLoading || availSaving || hasAnyError}
                  className="mt-5 w-full h-[40px] bg-[#8e2a2f] text-white rounded-lg font-bold text-[13px] hover:bg-[#7a2228] transition-colors disabled:opacity-50"
                >
                  {availSaving
                    ? 'Saving…'
                    : hasAnyError
                      ? 'Fix overlapping slots to save'
                      : 'Save availability'}
                </button>

                {/* Same-day (last-minute) booking toggle — saves on flip,
                    applies live (no admin approval). */}
                <div className="mt-6 border-t border-[#eee] pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-[#3a3335] text-[14px]">
                        Same-day bookings
                      </p>
                      <p className="text-[12px] text-[#908e8e] mt-1 leading-[18px]">
                        Let customers book you for last-minute appointments
                        today. When off, your slots for the current day are
                        hidden from search.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={sameDayEnabled}
                      onClick={handleToggleSameDay}
                      disabled={availLoading || sameDaySaving}
                      className={`relative shrink-0 w-[44px] h-[24px] rounded-full transition-colors disabled:opacity-50 ${
                        sameDayEnabled ? 'bg-[#8e2a2f]' : 'bg-[#d6d2d3]'
                      }`}
                    >
                      <span
                        className={`absolute top-[2px] left-[2px] w-[20px] h-[20px] bg-white rounded-full shadow transition-transform ${
                          sameDayEnabled
                            ? 'translate-x-[20px]'
                            : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Holidays */}
              <div className="border-t border-[#eee] pt-6 mb-6">
                <p className="font-bold text-[#3a3335] text-[14px] mb-4">
                  Holidays
                </p>
                <div className="flex gap-2">
                  <ThemedDatePicker
                    label="From"
                    value={holidayFrom}
                    onChange={setHolidayFrom}
                    className="flex-1"
                  />
                  <ThemedDatePicker
                    label="Until"
                    value={holidayUntil}
                    onChange={setHolidayUntil}
                    min={holidayFrom || undefined}
                    className="flex-1"
                  />
                </div>

                {/* Block specific hours instead of the whole day —
                    for a pre-existing external appointment. */}
                <label className="mt-3 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={holidayPartialDay}
                    onChange={(e) => setHolidayPartialDay(e.target.checked)}
                    className="accent-[#8e2a2f] w-4 h-4"
                  />
                  <span className="text-[12px] font-bold text-[#3a3335]">
                    Block specific hours only
                  </span>
                </label>

                {holidayPartialDay ? (
                  <div className="mt-2 flex gap-2">
                    <label className="flex-1 text-[11px] font-bold text-[#908e8e]">
                      From
                      <input
                        type="time"
                        value={holidayStartTime}
                        onChange={(e) => setHolidayStartTime(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#eee] rounded-lg px-3 py-2 text-[12px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                      />
                    </label>
                    <label className="flex-1 text-[11px] font-bold text-[#908e8e]">
                      Until
                      <input
                        type="time"
                        value={holidayEndTime}
                        onChange={(e) => setHolidayEndTime(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#eee] rounded-lg px-3 py-2 text-[12px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                      />
                    </label>
                  </div>
                ) : null}

                <input
                  type="text"
                  value={holidayReason}
                  onChange={(e) => setHolidayReason(e.target.value)}
                  maxLength={200}
                  placeholder="Reason (optional) — e.g. Family event"
                  className="mt-2 w-full bg-white border border-[#eee] rounded-lg px-4 py-2 text-[12px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                />

                <button
                  onClick={handleAddHoliday}
                  disabled={savingHoliday}
                  className="mt-3 w-full h-[40px] bg-[#8e2a2f] text-white rounded-lg font-bold text-[13px] hover:bg-[#7a2228] transition-colors disabled:opacity-50"
                >
                  {savingHoliday ? 'Saving…' : holidayPartialDay ? 'Block hours' : 'Add holiday'}
                </button>

                {holidayError ? (
                  <p className="mt-2 text-[#8e2a2f] text-[12px]">
                    {holidayError}
                  </p>
                ) : null}
                {holidayMessage ? (
                  <p className="mt-2 text-[#3a3335] text-[12px]">
                    {holidayMessage}
                  </p>
                ) : null}

                <div className="mt-5 space-y-2">
                  <p className="font-bold text-[#3a3335] text-[12px]">
                    Upcoming & past
                  </p>
                  {blocksLoading ? (
                    <p className="text-[#908e8e] text-[12px]">Loading…</p>
                  ) : timeBlocks.filter((b) => !isPatchTestBlock(b)).length ===
                    0 ? (
                    <p className="text-[#908e8e] text-[12px]">
                      No holidays scheduled.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {timeBlocks
                        .filter((b) => !isPatchTestBlock(b))
                        .map((b) => (
                        <li
                          key={b.id}
                          className="flex items-start justify-between gap-2 bg-white border border-[#eee] rounded-lg px-3 py-2"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-bold text-[#3a3335]">
                              {formatRange(b)}
                            </p>
                            {b.reason ? (
                              <p className="text-[11px] text-[#908e8e] truncate">
                                {b.reason}
                              </p>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCancelHoliday(b.id)}
                            disabled={cancellingId === b.id}
                            className="text-[#8e2a2f] text-[11px] font-bold hover:underline disabled:opacity-50"
                          >
                            {cancellingId === b.id ? 'Cancelling…' : 'Cancel'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Travel radius — editable here so pros can update their coverage
            without walking back through the sign-up flow. Applies live. */}
        <div className="mt-6 border border-[#eee] rounded-xl p-6">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2 className="font-bold text-[18px] text-[#3a3335]">
              Travel radius
            </h2>
            <span className="text-[#8e2a2f] font-extrabold text-[18px]">
              {radiusMiles} mi
            </span>
          </div>
          <p className="text-[13px] text-[#908e8e] mb-5 max-w-[640px]">
            How far you&apos;ll travel for a booking. Customers outside this
            range won&apos;t see you in search. Maximum {MAX_RADIUS_MILES}{' '}
            miles — beyond that isn&apos;t realistic with the 1-hour buffer
            between appointments.
          </p>
          <input
            type="range"
            min={0}
            max={MAX_RADIUS_MILES}
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(Number(e.target.value))}
            disabled={availLoading || radiusSaving}
            aria-label="Travel radius in miles"
            className="w-full accent-[#8e2a2f] cursor-pointer"
          />
          <div className="flex justify-between text-[11px] text-[#908e8e] mt-1">
            <span>0 mi</span>
            <span>{MAX_RADIUS_MILES} mi</span>
          </div>
          <button
            type="button"
            onClick={handleSaveRadius}
            disabled={
              availLoading || radiusSaving || radiusMiles === radiusInitial
            }
            className="mt-4 h-[40px] px-6 bg-[#8e2a2f] text-white rounded-lg font-bold text-[13px] hover:bg-[#7a2228] transition-colors disabled:opacity-50"
          >
            {radiusSaving ? 'Saving…' : 'Save travel radius'}
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
